import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Building2, 
  Shield, 
  FileText, 
  CreditCard, 
  Wallet, 
  Lock, 
  Users, 
  BarChart3,
  Server,
  TrendingUp
} from "lucide-react";
import { Link } from "react-router-dom";

const Services = () => {
  const services = [
    {
      title: "Bitcoin Balance Sheet Strategy",
      description: "Develop strategic approaches for adding Bitcoin to your business balance sheet across any industry.",
      icon: Building2,
      features: ["Portfolio diversification", "Asset allocation strategy", "Implementation planning"]
    },
    {
      title: "Corporate Bitcoin Policy Development",
      description: "Create comprehensive Bitcoin policies tailored for businesses entering the Bitcoin space.",
      icon: FileText,
      features: ["Custom policy creation", "Compliance guidelines", "Risk management frameworks"]
    },
    {
      title: "Bitcoin Collateralized Business Lending",
      description: "Navigate Bitcoin-backed lending solutions for business expansion and working capital needs.",
      icon: CreditCard,
      features: ["Loan structuring", "Risk evaluation", "Platform selection and vetting"]
    },
    {
      title: "Custody Solutions for Business",
      description: "Professional-grade Bitcoin storage solutions with institutional security standards for corporate holdings.",
      icon: Wallet,
      features: ["Multi-signature setup", "Hardware wallet configuration", "Cold storage solutions"]
    },
    {
      title: "Business Bitcoin Security",
      description: "Comprehensive security protocols to protect your company's Bitcoin holdings and digital assets.",
      icon: Shield,
      features: ["Security audits", "Best practice implementation", "Incident response planning"]
    },
    {
      title: "Bitcoin Node Setup for Businesses",
      description: "Set up and maintain Bitcoin nodes for enhanced privacy and network verification for your business.",
      icon: Server,
      features: ["Node setup and configuration", "Ongoing maintenance", "Privacy enhancement"]
    },
    {
      title: "Employee & Stakeholder Education",
      description: "Clear communication and education strategies for explaining Bitcoin adoption to your team and stakeholders.",
      icon: Users,
      features: ["Employee education programs", "Stakeholder presentations", "Custom training materials"]
    },
    {
      title: "Bitcoin Accounting & Reporting",
      description: "Accurate Bitcoin accounting and reporting for tax compliance and business financial management.",
      icon: BarChart3,
      features: ["Tax reporting compliance", "Accounting system integration", "Financial statement preparation"]
    }
  ];

  return (
    <section className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Bitcoin Education for Business Leaders
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Comprehensive Bitcoin advisory services designed for business leaders, CFOs, and companies across 
            all industries looking to add Bitcoin to their balance sheets as a strategic asset.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <Card key={index} className="h-full hover:shadow-soft transition-smooth border-border/50">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 bg-bitcoin-orange/10 rounded-lg">
                      <IconComponent className="h-6 w-6 text-bitcoin-orange" />
                    </div>
                    <TrendingUp className="h-4 w-4 text-primary" />
                  </div>
                  <CardTitle className="text-xl font-bold text-foreground">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-muted-foreground">
                        <div className="w-1.5 h-1.5 bg-bitcoin-orange rounded-full mr-3 flex-shrink-0"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Button variant="hero" size="lg" className="text-lg px-8 py-6" asChild>
            <Link to="/contact">
              Schedule a Consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;